import { defaultBlocksWithSavedGeneric, ownSavedBlocks } from "./_content-blocks.ts";
import { renderSignatureBlockHtml } from "./_signature-block.ts";

// --- App origin for in-email links (conversation page, genie page, etc.) ---
// Edge functions have no location context, so we always use the production domain.
// The browser preview also uses this — links in a preview email should point to prod anyway.
const _resolvedOrigin = "https://helpgenie.ai";

// Email assets are served from Supabase Storage (public bucket) so email clients
// can always fetch them, regardless of frontend deploy/SPA routing.
const ASSET_BASE = "https://vdskjkjxyeehklwxnhrf.supabase.co/storage/v1/object/public/email-assets";

// --- Types (duplicated for edge function context, mirrors src/types/email-templates.ts) ---

interface ContentBlock {
  id: string;
  type: string;
  enabled: boolean;
  order: number;
  label?: string;
  settings?: Record<string, any>;
}

interface ConversationReportConfig {
  version?: number;
  colors?: {
    primary?: string;
    accent?: string;
    text?: string;
    background?: string;
    headerColor?: string;
    sectionBackground?: string;
  };
  fonts?: {
    headingSize?: number;
    googleFont?: "Poppins" | "Roboto" | "Inter" | "Lato" | "Open Sans";
    family?: string;
  };
  // V1 legacy
  sections?: {
    showMetrics?: boolean;
    showLeadInfo?: boolean;
    showTopics?: boolean;
    showInsights?: boolean;
    showTranscript?: boolean;
  };
  branding?: {
    logoUrl?: string;
    companyName?: string;
  };
  buttons?: {
    buttonStyle?: "rounded" | "square" | "pill";
    borderRadius?: number;
    shadowEnabled?: boolean;
    buttonText?: string;
  };
  // V2
  blocks?: ContentBlock[];
}

const googleFontMap: Record<string, string> = {
  Poppins: "'Poppins', 'Segoe UI', Arial, sans-serif",
  Roboto: "'Roboto', 'Segoe UI', Arial, sans-serif",
  Inter: "'Inter', 'Segoe UI', Arial, sans-serif",
  Lato: "'Lato', 'Segoe UI', Arial, sans-serif",
  "Open Sans": "'Open Sans', 'Segoe UI', Arial, sans-serif",
};

interface ConversationReportData {
  conversation: any;
  aiResult: any;
  topics: any[];
  insights: string[];
  transcript: any[];
  timezone?: string;
  redirect_url?: string;
  escalationReason?: string | null;
  escalationSeverity?: "low" | "medium" | "high" | null;
}

// --- Default block labels ---
const DEFAULT_LABELS: Record<string, string> = {
  urgency_banner: "Urgency Banner",
  escalation_reason: "Escalation Reason",
  metrics: "Key Performance Metrics",
  lead_info: "Insights",
  topics: "Main Topics",
  insights: "Key Points",
  transcript: "Conversation Transcript",
  mobile_app_promo: "Help Genie in your pocket",
};

// Hex → rgba helper for tenant-brand-tracking tints across all renderers.
// Replaces hardcoded rgba(78,156,255,*) and rgba(255,77,184,*) leaks.
function brandHexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substr(0, 2), 16);
  const g = parseInt(clean.substr(2, 2), 16);
  const b = parseInt(clean.substr(4, 2), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// --- V1 → V2 migration for escalation alerts ---
function resolveBlocks(config: ConversationReportConfig | null | undefined): ContentBlock[] {
  // Signature depends on genie branding a tenant may not have configured, so
  // it starts OFF when backfilled — unlike the rest of the value layer.
  const VALUE_LAYER_AUTOINJECT: string[] = ["signature"];
  const VALUE_LAYER_DEFAULT_DISABLED = new Set(["signature"]);

  // Escalation-defining blocks — a saved config missing these (e.g. the
  // agent's shared brand-template row, which carries no type-specific blocks)
  // renders as an undifferentiated conversation report with no indication
  // it's an escalation. Always led in at the front, ahead of everything else,
  // unlike the rest of the value layer which appends at the end.
  const LEADING_AUTOINJECT: string[] = ["urgency_banner", "escalation_reason"];

  // V1 fallback / built-in default: urgency banner + escalation reason up
  // front, then the same section shape as the conversation report.
  const sections = config?.sections || {};
  const base: ContentBlock[] = [
    { id: "urgency_banner", type: "urgency_banner", enabled: true, order: 0 },
    { id: "escalation_reason", type: "escalation_reason", enabled: true, order: 1 },
    { id: "metrics", type: "metrics", enabled: sections.showMetrics !== false, order: 2 },
    { id: "lead_info", type: "lead_info", enabled: sections.showLeadInfo !== false, order: 3 },
    { id: "topics", type: "topics", enabled: sections.showTopics !== false, order: 4 },
    { id: "insights", type: "insights", enabled: sections.showInsights !== false, order: 5 },
    { id: "transcript", type: "transcript", enabled: sections.showTranscript !== false, order: 6 },
    { id: "mobile_app_promo", type: "mobile_app_promo", enabled: true, order: 7 },
    { id: "signature", type: "signature", enabled: false, order: 8 },
  ];

  const savedBlocks = ownSavedBlocks(config, base);
  if (!savedBlocks) return defaultBlocksWithSavedGeneric(config, base);

  let blocks = [...savedBlocks].sort((a, b) => a.order - b.order);
  const existing = new Set(blocks.map((b) => b.type));

  const missingLeading = LEADING_AUTOINJECT.filter((type) => !existing.has(type));
  if (missingLeading.length > 0) {
    const leadingBlocks = missingLeading.map((type) => ({ id: type, type, enabled: true, order: 0 }));
    blocks = [...leadingBlocks, ...blocks];
    missingLeading.forEach((type) => existing.add(type));
  }

  let maxOrder = blocks.length > 0 ? Math.max(...blocks.map((b) => b.order)) : -1;
  for (const type of VALUE_LAYER_AUTOINJECT) {
    if (existing.has(type)) continue;
    maxOrder += 1;
    blocks.push({ id: type, type, enabled: !VALUE_LAYER_DEFAULT_DISABLED.has(type), order: maxOrder });
    existing.add(type);
  }

  blocks = blocks.map((b, i) => ({ ...b, order: i }));
  return pinSignatureLast(blocks);
}

// The signature depends on genie/team branding and reads as a sign-off — it
// must always render as the final section, regardless of where `order`
// (user-editable via drag-and-drop reorder in the Report Sections UI, or a
// stale saved config) places it. Sorting by `order` alone isn't enough to
// guarantee that, so pin it last as an explicit final step.
function pinSignatureLast(blocks: ContentBlock[]): ContentBlock[] {
  const signatureIndex = blocks.findIndex((b) => b.type === "signature");
  if (signatureIndex === -1 || signatureIndex === blocks.length - 1) return blocks;
  const withoutSignature = blocks.filter((b) => b.type !== "signature");
  return [...withoutSignature, blocks[signatureIndex]];
}

// --- Shared style context passed to all renderers ---
interface StyleContext {
  colors: {
    primary: string;
    accent: string;
    text: string;
    background: string;
    headerColor: string;
    sectionBackground: string;
  };
  fontFamily: string;
  borderRadius: number;
  metricButtonRadius: string;
  boxShadow: string;
  shadowEnabled: boolean;
}

// --- Block Renderers ---

function renderMetrics(block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  const { aiResult } = data;
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.metrics;

  const sentimentScore = aiResult?.sentiment_analysis?.overall_sentiment_score;
  const agentTalk = aiResult?.talk_metrics?.agent_talk_percentage;
  const customerTalk = aiResult?.talk_metrics?.customer_talk_percentage;
  const satisfaction = aiResult?.sentiment_analysis?.customer_satisfaction_score;

  const formatValue = (val: any, suffix = "") => {
    if (val === undefined || val === null) return "N/A";
    return typeof val === "number" ? `${val.toFixed(2)}${suffix}` : `${val}${suffix}`;
  };

  const numberColor = colors.headerColor || "#28406F";
  const cardBorder = "#E5EAF0";
  const labelColor = "#6B7280";

  const cell = (value: string, cellLabel: string) => `
    <td width="50%" style="padding: 3px;" valign="top">
      <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: #FFFFFF; border: 1px solid ${cardBorder}; border-radius: ${metricButtonRadius}; overflow: hidden;">
        <tr>
          <td style="height: 2px; background: ${colors.primary}; line-height: 2px; font-size: 0;">&nbsp;</td>
        </tr>
        <tr>
          <td style="padding: 10px 12px 9px 12px;" valign="top">
            <div style="color: ${numberColor}; font-size: 19px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; line-height: 1; margin: 0 0 3px 0;">${value}</div>
            <div style="color: ${labelColor}; font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.6px;">${cellLabel}</div>
          </td>
        </tr>
      </table>
    </td>
  `;

  return `
    <tr>
      <td style="padding: 8px 24px 4px 24px;">
        <h3 style="color: ${numberColor}; margin: 0 0 8px 0; font-size: 13px; font-weight: 600; font-family: ${fontFamily}; letter-spacing: 0.2px; text-transform: uppercase;">
          ${label}
        </h3>
        <table cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            ${cell(formatValue(sentimentScore), "Overall Sentiment")}
            ${cell(formatValue(agentTalk, "%"), "Agent Talk Time")}
          </tr>
          <tr>
            ${cell(formatValue(customerTalk, "%"), "Customer Talk Time")}
            ${cell(formatValue(satisfaction, "%"), "Customer Satisfaction")}
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderMobileAppPromo(block: ContentBlock, _data: ConversationReportData, styles: StyleContext): string {
  const settings = block.settings || {};
  const { colors, fontFamily, metricButtonRadius } = styles;
  // Conversation report goes to the BUSINESS customer (HG's customer reviewing how
  // their agent performed). The CTA is about sharing the app with their team and
  // their end customers.
  const heading = settings.heading || "Share Help Genie with your customers";
  const body = settings.body || "Get your team and customers on the app for instant support on every product.";
  // TODO_APP_STORE_URL / TODO_PLAY_STORE_URL — Sam to drop real store URLs.
  const appStoreUrl = settings.appStoreUrl || "https://helpgenie.ai/app";
  const playStoreUrl = settings.playStoreUrl || "https://helpgenie.ai/app";
  // Mascot is optional. Defaults to the HG mascot; rebranded tenants can override
  // via settings.mascotUrl or pass '' to hide it entirely.
  const mascotUrl = settings.mascotUrl !== undefined ? settings.mascotUrl : `${ASSET_BASE}/mascot-thumbsup.png`;
  const headerColor = colors.headerColor || colors.text || "#28406F";

  const mascotCell = mascotUrl
    ? `<td valign="middle" width="60" style="padding-right: 10px;"><img src="${mascotUrl}" width="48" alt="" style="display: block; width: 48px; height: auto;" /></td>`
    : "";

  return `
    <tr>
      <td style="padding: 6px 24px 14px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: #FFFFFF; border: 1px solid #E5EAF0; border-radius: ${metricButtonRadius}; overflow: hidden;">
          <tr>
            <td style="height: 2px; background: ${colors.primary}; line-height: 2px; font-size: 0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding: 12px 14px;" valign="middle">
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  ${mascotCell}
                  <td valign="middle">
                    <div style="color: ${headerColor}; font-size: 14px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; margin: 0 0 2px 0; line-height: 1.2;">${heading}</div>
                    <div style="color: #6B7280; font-size: 12px; line-height: 1.4; font-family: ${fontFamily}; margin: 0 0 10px 0;">${body}</div>
                    <table cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td style="padding-right: 8px;">
                          <a href="${appStoreUrl}" style="display: inline-block;">
                            <img src="${ASSET_BASE}/app-store-badge.svg" alt="Download on the App Store" width="120" height="36" style="display: block; width: 120px; height: 36px; border: 0;" />
                          </a>
                        </td>
                        <td>
                          <a href="${playStoreUrl}" style="display: inline-block;">
                            <img src="${ASSET_BASE}/google-play-badge.png" alt="Get it on Google Play" width="120" height="36" style="display: block; width: 120px; height: 36px; border: 0;" />
                          </a>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderSectionCard(label: string, contentHtml: string, styles: StyleContext): string {
  const { fontFamily, borderRadius, colors } = styles;

  // Convert section background hex to rgba with transparency
  const hexToRgba = (hex: string, alpha: number): string => {
    const cleanHex = hex.replace("#", "");
    const r = parseInt(cleanHex.substr(0, 2), 16);
    const g = parseInt(cleanHex.substr(2, 2), 16);
    const b = parseInt(cleanHex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const sectionBgHex = colors.sectionBackground || "#FAFBFC";
  const sectionBgRgba = hexToRgba(sectionBgHex, 0.2);

  // Use a slightly higher opacity for header background
  const headerBgRgba = hexToRgba(sectionBgHex, 0.3);
  // If background is default, use E5EAF0 for border; otherwise use the custom color
  const borderColor = sectionBgHex === "#FAFBFC" ? "#E5EAF0" : sectionBgHex;

  return `
    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom: 25px; border: 1px solid ${borderColor}; background-color: ${sectionBgRgba}; border-radius: ${borderRadius}px; overflow: hidden;">
      <tr>
        <td style="background-color: ${headerBgRgba}; padding: 15px 20px; border-bottom: 1px solid ${borderColor};">
          <h3 style="color: ${colors.text}; margin: 0; font-size: 18px; font-weight: 600; font-family: ${fontFamily};">${label}</h3>
        </td>
      </tr>
      <tr>
        <td style="padding: 20px; background-color: ${sectionBgRgba};">
          <div style="font-size: 14px; line-height: 1.6;">
            ${contentHtml}
          </div>
        </td>
      </tr>
    </table>`;
}

function renderLeadInfo(block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  const { aiResult } = data;
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.lead_info;
  const leadInfo = aiResult?.lead_info || {};
  const headerColor = colors.headerColor;

  const contentHtml =
    Object.keys(leadInfo).length > 0
      ? Object.entries(leadInfo)
          .map(([key, value]) => {
            const displayValue = Array.isArray(value)
              ? value.length > 0
                ? value.join(", ")
                : "None"
              : value !== null && value !== undefined
                ? String(value)
                : "None";
            return `<div style="margin-bottom: 6px; color: ${colors.text}; font-size: 12px; font-family: ${fontFamily};"><strong style="color: ${headerColor};">${key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())}:</strong> ${displayValue}</div>`;
          })
          .join("")
      : `<div style="font-size: 12px; color: #7C8B9C; font-family: ${fontFamily};">No insights extracted</div>`;

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 12px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              ${contentHtml}
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderTopics(block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  const { topics } = data;
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.topics;
  const headerColor = colors.headerColor;

  const primaryHex = (colors.primary || "#4E9CFF").replace("#", "");
  const pr = parseInt(primaryHex.substr(0, 2), 16);
  const pg = parseInt(primaryHex.substr(2, 2), 16);
  const pb = parseInt(primaryHex.substr(4, 2), 16);
  const chipBg = `rgba(${pr}, ${pg}, ${pb}, 0.08)`;
  const chipBorder = `rgba(${pr}, ${pg}, ${pb}, 0.22)`;

  const contentHtml =
    topics.length > 0
      ? `<table cellpadding="0" cellspacing="5" border="0" width="100%"><tr><td>${topics
          .map(
            (topic) =>
              `<table cellpadding="0" cellspacing="0" border="0" style="display: inline-table; margin: 2px;"><tr><td style="padding: 5px 11px; background-color: ${chipBg}; color: ${colors.primary}; font-size: 12px; font-weight: 500; border: 1px solid ${chipBorder}; border-radius: 999px; text-align: center; font-family: ${fontFamily};">${topic}</td></tr></table>`,
          )
          .join(" ")}</td></tr></table>`
      : `<div style="font-size: 12px; color: #7C8B9C; font-family: ${fontFamily};">No topics detected</div>`;

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 12px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              ${contentHtml}
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderInsights(block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  const { insights } = data;
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.insights;
  const headerColor = colors.headerColor;

  const contentHtml =
    insights.length > 0
      ? insights
          .map(
            (insight) =>
              `<div style="margin-bottom: 8px; color: ${colors.text}; font-size: 12px; font-family: ${fontFamily}; padding-left: 16px; position: relative; line-height: 1.5;"><span style="color: ${colors.primary}; font-weight: 700; position: absolute; left: 0;">&bull;</span>${insight}</div>`,
          )
          .join("")
      : `<div style="font-size: 12px; color: #7C8B9C; font-family: ${fontFamily};">No insights available</div>`;

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 12px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              ${contentHtml}
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderTranscript(block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  const { transcript } = data;
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.transcript;
  const headerColor = colors.headerColor;

  const contentHtml =
    transcript.length > 0
      ? transcript
          .map((entry) => {
            const speaker = entry.role || entry.speaker || "user";
            const message = entry.message || entry.text || entry.content || "";
            return `<div style="margin-bottom: 10px; font-size: 12px; color: #7C8B9C; font-family: ${fontFamily}; line-height: 1.5;">
              <strong style="color: ${headerColor};">${speaker === "user" ? "User" : "Agent"}:</strong> ${message}
            </div>`;
          })
          .join("")
      : `<div style="font-size: 12px; color: #7C8B9C; font-family: ${fontFamily};">No transcript available</div>`;

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 12px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              ${contentHtml}
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderCustomText(block: ContentBlock, _data: ConversationReportData, styles: StyleContext): string {
  const settings = block.settings || {};
  const title = settings.title || "";
  const body = settings.body || "";
  const alignment = settings.alignment || "left";
  if (!title && !body) return "";

  const titleHtml = title
    ? `<div style="margin-bottom: 8px; font-weight: 600; color: ${styles.colors.text};">${title}</div>`
    : "";
  const bodyHtml = body ? `<div style="color: ${styles.colors.text};">${body}</div>` : "";

  const contentHtml = `<div style="text-align: ${alignment};">${titleHtml}${bodyHtml}</div>`;
  const label = block.label || "Custom Section";

  return `<tr><td style="padding: 0 30px 0 30px;">${renderSectionCard(label, contentHtml, styles)}</td></tr>`;
}

function renderCtaButton(block: ContentBlock, _data: ConversationReportData, styles: StyleContext): string {
  const settings = block.settings || {};
  const url = settings.url || "#";
  const text = settings.text || "Click Here";
  const { colors, fontFamily, metricButtonRadius } = styles;

  return `
    <tr>
      <td style="padding: 10px 30px 25px 30px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            <td align="center">
              <a href="${url}" style="display: inline-block; padding: 12px 28px; background-color: ${colors.primary}; color: #FFFFFF; text-decoration: none; font-weight: 600; font-size: 14px; border-radius: ${metricButtonRadius}; font-family: ${fontFamily}; box-shadow: ${styles.shadowEnabled ? `0 4px 12px ${brandHexToRgba(colors.primary, 0.3)}` : "none"};">${text}</a>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderDivider(_block: ContentBlock, _data: ConversationReportData, styles: StyleContext): string {
  return `
    <tr>
      <td style="padding: 10px 30px;">
        <div style="width: 100%; height: 1px; background-color: #E5EAF0;"></div>
      </td>
    </tr>`;
}

function renderImage(block: ContentBlock, _data: ConversationReportData, _styles: StyleContext): string {
  const settings = block.settings || {};
  const url = settings.url || "";
  const altText = settings.altText || "";
  const maxWidth = settings.maxWidth || 540;
  const alignment = settings.alignment || "center";
  if (!url) return "";

  return `
    <tr>
      <td style="padding: 10px 30px;" align="${alignment}">
        <img src="${url}" alt="${altText}" style="max-width: ${maxWidth}px; width: 100%; height: auto; display: block;" />
      </td>
    </tr>`;
}


// --- Escalation-specific renderers ---

function severityColor(severity: string | null | undefined, styles: StyleContext): string {
  switch (severity) {
    case "high":
      return "#DC2626";
    case "medium":
      return "#D97706";
    case "low":
      return "#6B7280";
    default:
      return styles.colors.accent;
  }
}

function renderUrgencyBanner(_block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  const severity = data.escalationSeverity || "medium";
  const color = severityColor(severity, styles);
  const label = severity.charAt(0).toUpperCase() + severity.slice(1);

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: ${color}1A; border: 1px solid ${color}55; border-radius: ${styles.metricButtonRadius}; overflow: hidden;">
          <tr>
            <td style="padding: 12px 16px;">
              <div style="font-size: 12px; font-weight: 700; color: ${color}; text-transform: uppercase; letter-spacing: 0.6px; font-family: ${styles.fontFamily};">
                &#9888; This conversation needs your attention &middot; ${label} priority
              </div>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderEscalationReason(block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.escalation_reason;
  const reason = data.escalationReason || "No reason provided";

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 12px 16px;">
              <h3 style="color:${colors.headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <div style="font-size: 13px; color: ${colors.text}; font-family: ${fontFamily}; line-height: 1.5;">${reason}</div>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderSignature(_block: ContentBlock, data: ConversationReportData, _styles: StyleContext): string {
  const agent = data.conversation?.agent || {};
  const html = renderSignatureBlockHtml(agent.name || "Your Genie", agent.description, agent.branding);
  if (!html) return "";
  return `<tr><td style="padding: 6px 24px 14px 24px;">${html}</td></tr>`;
}

// --- Block Renderer Registry ---
const blockRenderers: Record<
  string,
  (block: ContentBlock, data: ConversationReportData, styles: StyleContext) => string
> = {
  urgency_banner: renderUrgencyBanner,
  escalation_reason: renderEscalationReason,
  signature: renderSignature,
  metrics: renderMetrics,
  lead_info: renderLeadInfo,
  topics: renderTopics,
  insights: renderInsights,
  transcript: renderTranscript,
  mobile_app_promo: renderMobileAppPromo,
  custom_text: renderCustomText,
  cta_button: renderCtaButton,
  divider: renderDivider,
  image: renderImage,
};


export function generateEscalationAlertEmailConfigurable(
  data: ConversationReportData,
  config?: ConversationReportConfig | null,
): string {
  const { conversation, timezone = "UTC" } = data;

  const defaults = {
    colors: {
      primary: "#4E9CFF",
      accent: "#FF4DB8",
      text: "#1A1A1A",
      background: "#F9FAFC",
      headerColor: undefined as string | undefined,
    },
    fonts: {
      family: "'Poppins', 'Segoe UI', Arial, sans-serif",
      headingSize: 32,
    },
    branding: {
      logoUrl: `${ASSET_BASE}/wordmark.png`,
      companyName: "Help Genie",
    },
    buttons: {
      buttonStyle: "rounded" as const,
      borderRadius: 8,
      shadowEnabled: true,
      buttonText: "View Full Conversation",
    },
  };

  const colors = { ...defaults.colors, ...config?.colors };
  const fonts = { ...defaults.fonts, ...config?.fonts };
  const branding = { ...defaults.branding, ...config?.branding };
  const buttons = { ...defaults.buttons, ...config?.buttons };

  const googleFontName = fonts.googleFont || "Poppins";
  const fontFamily = fonts.family || googleFontMap[googleFontName];
  const googleFontUrl = `https://fonts.googleapis.com/css2?family=${googleFontName}:wght@400;500;600;700&family=Baloo+Paaji+2:wght@700;800&display=swap`;

  const getButtonRadius = () => {
    switch (buttons.buttonStyle) {
      case "square":
        return "0px";
      case "pill":
        return "999px";
      case "rounded":
      default:
        return `${buttons.borderRadius}px`;
    }
  };

  // Header color cascade: explicit headerColor > tenant text color > HG navy.
  // Monochrome rebrands (all-black NZ Funds Advisor etc.) should not inherit
  // the HG navy default from the base template.
  const headerTextColor = colors.headerColor || colors.text || "#28406F";
  const boxShadow = buttons.shadowEnabled
    ? `0 4px 20px ${brandHexToRgba(colors.primary || defaults.colors.primary, 0.1)}`
    : "none";
  const metricButtonRadius = getButtonRadius();

  const styleContext: StyleContext = {
    colors: {
      primary: colors.primary || defaults.colors.primary,
      accent: colors.accent || defaults.colors.accent,
      text: colors.text || defaults.colors.text,
      background: colors.background || defaults.colors.background,
      headerColor: headerTextColor,
      sectionBackground: colors.sectionBackground || "#FAFBFC",
    },
    fontFamily,
    borderRadius: buttons.borderRadius || defaults.buttons.borderRadius,
    metricButtonRadius,
    boxShadow,
    shadowEnabled: buttons.shadowEnabled ?? defaults.buttons.shadowEnabled,
  };

  // Resolve blocks (V2 or V1 migration)
  const blocks = resolveBlocks(config);
  const enabledBlocks = blocks.filter((b) => b.enabled);

  // Split around the signature block (always last in enabledBlocks, see
  // pinSignatureLast in resolveBlocks) so other content — like the redirect
  // button below — can be inserted before it without pushing it out of the
  // final position.
  const renderBlock = (block: ContentBlock) => {
    const renderer = blockRenderers[block.type];
    return renderer ? renderer(block, data, styleContext) : "";
  };
  const mainBlocks = enabledBlocks.filter((b) => b.type !== "signature");
  const signatureBlocks = enabledBlocks.filter((b) => b.type === "signature");
  const blocksHtml = mainBlocks.map(renderBlock).filter(Boolean).join("\n");
  const signatureHtml = signatureBlocks.map(renderBlock).filter(Boolean).join("\n");

  // The genie's own branded signature (when configured) replaces the fixed
  // Help Genie footer below rather than stacking under it.
  const hasCustomSignature = Boolean(
    enabledBlocks.some((b) => b.type === "signature") && data.conversation?.agent?.branding?.signatureStyle?.enabled,
  );

  const getDurationText = () => {
    try {
      const duration =
        conversation.details?.duration ||
        conversation.duration ||
        conversation.metadata?.duration_seconds ||
        conversation.metadata?.call_duration_secs ||
        conversation.metadata?.call_duration;
      if (duration && !isNaN(duration) && duration > 0) {
        return `${Math.floor(duration / 60)}m ${duration % 60}s`;
      }
      return "N/A";
    } catch {
      return "N/A";
    }
  };

  const getFormattedDate = () => {
    try {
      let dateValue = null;
      if (conversation.details?.conversation_date) {
        dateValue = new Date(conversation.details?.conversation_date);
      } else if (conversation.created_at) {
        dateValue = new Date(conversation.created_at);
      } else if (conversation.start_time) {
        dateValue = new Date(conversation.start_time);
      } else if (conversation.metadata?.start_time_unix_secs) {
        dateValue = new Date(conversation.metadata.start_time_unix_secs * 1000);
      }
      if (dateValue && !isNaN(dateValue.getTime())) {
        const formatter = new Intl.DateTimeFormat("en-US", {
          timeZone: timezone,
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        return `${formatter.format(dateValue)} ${timezone}`;
      }
      return "N/A";
    } catch {
      return "N/A";
    }
  };

  // Deep link to the full conversation analysis page in the Help Genie app.
  // Rendered as a small solid pill in the literal top-right of the email,
  // next to the logo. Solid primary tracks each tenant's brand and matches
  // the title underline + footer band, so it never reads as a stock blue CTA.
  const conversationDetailUrl = conversation?.id
    ? `${_resolvedOrigin}/genie-hub/conversations/${conversation.id}`
    : null;
  const viewFullAnalysisChip = conversationDetailUrl
    ? `<a href="${conversationDetailUrl}" style="display: inline-block; padding: 9px 16px; background-color: ${styleContext.colors.primary}; color: #FFFFFF; text-decoration: none; font-weight: 600; font-size: 12px; border-radius: 999px; font-family: ${fontFamily}; letter-spacing: 0.2px; white-space: nowrap; line-height: 1;">View full analysis &rarr;</a>`
    : "";

  // "View Full Conversation" button — links to the actual conversation report
  // page (same URL as the viewFullAnalysisChip above), not agent.redirect_url:
  // that field is the genie's own post-conversation redirect (e.g. a
  // thank-you page a tenant configures for their voice/chat widget), and a
  // button labeled "View Full Conversation" pointing there was a mislabeled
  // bug (same issue reported by support for conversation_report's identically
  // shaped button). Only rendered if there's no explicit cta_button block, so
  // a tenant who wants their own redirect CTA adds one instead of this
  // default button being repurposed for it.
  const hasCtaBlock = enabledBlocks.some((b) => b.type === "cta_button");
  const redirectButtonHtml =
    conversationDetailUrl && !hasCtaBlock
      ? `
      <tr>
        <td style="padding: 10px 30px 25px 30px;">
          <table cellpadding="0" cellspacing="0" border="0" width="100%">
            <tr>
              <td align="center">
                <a href="${conversationDetailUrl}" style="display: inline-block; padding: 12px 28px; background-color: ${styleContext.colors.primary}; color: #FFFFFF; text-decoration: none; font-weight: 600; font-size: 14px; border-radius: ${metricButtonRadius}; font-family: ${fontFamily}; box-shadow: ${buttons.shadowEnabled ? `0 4px 12px ${brandHexToRgba(styleContext.colors.primary, 0.3)}` : "none"};">${buttons.buttonText}</a>
              </td>
            </tr>
          </table>
        </td>
      </tr>`
      : "";

  return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Conversation Escalation Alert</title>
        <link rel="stylesheet" href="${googleFontUrl}" type="text/css">
        <style>@import url('${googleFontUrl}');</style>
        <!--[if mso]>
        <noscript>
          <xml>
            <o:OfficeDocumentSettings>
              <o:PixelsPerInch>96</o:PixelsPerInch>
            </o:OfficeDocumentSettings>
          </xml>
        </noscript>
        <![endif]-->
      </head>
      <body style="margin: 0; padding: 0; background-color: #F5F5F5; font-family: ${fontFamily}; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%;">

        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #F5F5F5; margin: 0; padding: 0;">
          <tr>
            <td align="center" style="padding: 12px;">

              <table cellpadding="0" cellspacing="0" border="0" width="600" style="max-width: 600px; background-color: ${styleContext.colors.background}; box-shadow: ${boxShadow}; border-radius: ${styleContext.borderRadius}px; overflow: hidden;">

                <tr>
                  <td style="background-color: ${styleContext.colors.background}; padding: 20px 24px 4px 24px;">
                    <table cellpadding="0" cellspacing="0" border="0" width="100%">
                      <tr>
                        <td valign="middle">
                          <img src="${branding.logoUrl}" alt="${branding.companyName}" style="max-height: 28px; max-width: 160px; display: block;" />
                        </td>
                        <td valign="middle" align="right" style="padding-left: 12px; white-space: nowrap;">
                          ${viewFullAnalysisChip}
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td style="background-color: ${styleContext.colors.background}; padding: 10px 24px 16px 24px;">
                    <h1 style="color: ${headerTextColor}; margin: 0; font-size: ${Math.max(20, (fonts.headingSize ?? 32) - 8)}px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; line-height: 1.15; letter-spacing: -0.2px;">Conversation Escalation Alert</h1>
                    <div style="width: 44px; height: 2px; background: ${styleContext.colors.primary}; margin: 8px 0 0; border-radius: 2px;"></div>
                  </td>
                </tr>

                <tr>
                  <td style="padding: 0 24px 10px 24px;">
                    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: ${(() => {
                      const sb = styleContext.colors.sectionBackground;
                      const hex = sb.replace("#", "");
                      const r = parseInt(hex.substr(0, 2), 16);
                      const g = parseInt(hex.substr(2, 2), 16);
                      const b = parseInt(hex.substr(4, 2), 16);
                      return `rgba(${r}, ${g}, ${b}, 0.2)`;
                    })()}; border: 1px solid ${styleContext.colors.sectionBackground === "#FAFBFC" ? "#E5EAF0" : styleContext.colors.sectionBackground}; border-radius: ${styleContext.borderRadius}px;">
                      <tr>
                        <td style="padding: 14px 18px; color: ${headerTextColor};">
                          <h2 style="color: ${headerTextColor}; margin: 0 0 8px 0; font-size: 17px; font-weight: 600; font-family: ${fontFamily};">${
                            conversation.agent?.name || "Genie"
                          }</h2>
                          <table cellpadding="0" cellspacing="0" border="0" width="100%">
                            <tr>
                              <td style="width: 50%; font-size: 12px; color: ${styleContext.colors.text}; padding-right: 8px;">
                                <strong style="color: ${headerTextColor}; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px;">Date</strong><br>
                                ${getFormattedDate()}
                              </td>
                              <td style="width: 50%; font-size: 12px; color: ${styleContext.colors.text}; padding-left: 8px;">
                                <strong style="color: ${headerTextColor}; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px;">Duration</strong><br>
                                ${getDurationText()}
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>

                ${blocksHtml}

                ${redirectButtonHtml}

                ${signatureHtml}

                ${hasCustomSignature ? "" : `
                <tr>
                  <td align="center" style="padding: 18px 24px; background: ${styleContext.colors.primary};">
                    <div style="color: #FFFFFF; font-size: 18px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; letter-spacing: 0.2px; margin: 0 0 4px 0;">
                      ${branding.companyName}
                    </div>
                    <div style="color: rgba(255,255,255,0.96); font-size: 12px; font-weight: 500; font-family: ${fontFamily}; margin-bottom: 10px;">
                      Goodbye manuals. Hello Help Genie.
                    </div>
                    <div style="color: rgba(255,255,255,0.85); font-size: 11px; font-family: ${fontFamily};">
                      <a href="https://helpgenie.ai" style="color: #FFFFFF; text-decoration: none; font-weight: 500;">helpgenie.ai</a>
                      &nbsp;&nbsp;&middot;&nbsp;&nbsp; +64 9 886 4856
                    </div>
                  </td>
                </tr>
                `}

              </table>

            </td>
          </tr>
        </table>

      </body>
      </html>
    `;
}
